import { Icon, Input, Button } from 'Components';
import { useEffect, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import moment from 'moment';
import { debounce } from 'lodash';
import { CampaignThresholdData } from '../..';
import TemplateCard from '../../components/TemplateCard';
import {
	DEFAULT_TEMPLATES_PAGESIZE,
	DEFAULT_WHITELIST_URL
} from '../../PhishingManager.constants';
import ThreeMonthWhitelistingModal from '../CampaignSteps/components/ThreeMonthWhitelistingModal';
import FirstWhitelistingModal from '../CampaignSteps/components/FirstWhitelistingModal';
import { FilterResults } from '../CampaignSteps';
import { getPhishingCampaignTemplates } from '../../service/index.Service';
import { capitalize, errorMessageHandler, joinItems } from '../../utils';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';
import { Template } from 'PhishingManager/components/TemplateCard';
import PaginationElement from 'PhishingManager/components/Pagination';
import { useTranslation } from 'react-i18next';

type Step1Props = {
	selectedTemplate: string | number | null;
	tenantId: string | number | undefined;
	onSelect: (value: string | number | null) => void;
	onNext: () => void;
	onBack?: () => void;
	onClose: () => void;
	thresholdData: null | CampaignThresholdData;
	filterResults: FilterResults | null;
	step1State: {
		search: string;
		language: string;
		category: string;
		difficulty: string;
		templatePage: number;
	};
	setStep1State: React.Dispatch<React.SetStateAction<any>>;
	setToast: (
		toast: { message: string; type: 'warning' | 'success' }
	) => void;
	isMobile: boolean;
};

interface DropdownProps {
	value: string | number;
	label: string;
}

interface ApiTemplate {
	id: number;
	name: string;
	img_path: string;
	difficulties: string;
	languages: number[];
	categories: number[];
}

export default function Step1({
	selectedTemplate,
	filterResults,
	onSelect,
	onNext,
	onBack,
	onClose,
	setToast,
	thresholdData,
	step1State,
	setStep1State,
	tenantId,
	isMobile
}: Step1Props) {
	const [languages, setLanguages] = useState<DropdownProps[]>([]);
	const [difficulties, setDifficulties] = useState<DropdownProps[]>([]);
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
	const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

	const { search, language, category, difficulty, templatePage } = step1State;
	const pageSize = DEFAULT_TEMPLATES_PAGESIZE;

	const [
		openFirstWhitelistingModal,
		setOpenFirstWhitelistingModal
	] = useState(false);
	const [
		openThreeMonthWhitelistingModal,
		setOpenThreeMonthWhitelistingModal
	] = useState(false);
	const [hasShownWhitelisting, setHasShownWhitelisting] = useState(false);
	const [templates, setTemplates] = useState<Template[]>([]);
	const [totalTemplates, setTotalTemplates] = useState(0);
	const [loading, setLoading] = useState(false);
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const { t } = useTranslation();

	const debouncedSetSearch = useMemo(
		() =>
			debounce((value: string) => {
				setDebouncedSearch(value);
			}, 500),
		[]
	);

	const handleCloseFirstModal = () => {
		setOpenFirstWhitelistingModal(false);
	};

	const handleConfirmFirstModal = () => {
		window.open(DEFAULT_WHITELIST_URL, '_blank', 'noopener,noreferrer');
		setOpenFirstWhitelistingModal(false);
	};

	const selectedLanguageIds = filterResults?.languages
		?.filter((l) => l.name === language)
		.map((l) => l.id);

	const selectedCategoryIds = filterResults?.categories
		?.filter((c) => c.name === category)
		.map((c) => c.id);

	const fetchCampaignsTemplates = async () => {
		try {
			const hasFilters =
				!!difficulty ||
				(selectedLanguageIds?.length ?? 0) > 0 ||
				(selectedCategoryIds?.length ?? 0) > 0;
			if (search && search.length <= 2 && !hasFilters) {
				return;
			}
			setLoading(true);
			const response = await getPhishingCampaignTemplates(tenantId, {
				name: search && search.length > 2 ? search : undefined,
				difficulties: difficulty ? [difficulty] : undefined,
				languages: selectedLanguageIds,
				categories: selectedCategoryIds,
				page: templatePage,
				limit: pageSize
			});

			const mappedTemplates: Template[] = response.data.map(
				(el: ApiTemplate) => ({
					value: el.id,
					label: el.name,
					categories: el.categories,
					languages: el.languages,
					difficulty: capitalize(el.difficulties),
					img: el.img_path
				})
			);

			mappedTemplates.sort((a, b) =>
				sortOrder === 'asc' ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label)
			);

			setTemplates((prev) => {
				if (!isMobile || templatePage === 1) {
					return mappedTemplates;
				}
				return [...prev, ...mappedTemplates];
			});

			setTotalTemplates(response.meta.total);
		} catch (err: any) {
			const errorMessage = errorMessageHandler(err, `phishingManager.errors.failedStep2`);
			setToast({ message: t(errorMessage), type: 'warning' });
			setTemplates([]);
			setTotalTemplates(0);
		} finally {
			setLoading(false);
		}
	};

	const handleCloseThreeMonthModal = () => {
		setOpenThreeMonthWhitelistingModal(false);
	};

	const shouldShowThreeMonthModal = (firstCampaignDate: string) => {
		const start = moment(firstCampaignDate);
		const now = moment();
		if (now.isBefore(start.clone().add(3, 'months'), 'day')) {
			return false;
		}
		const cyclesPassed = Math.floor(now.diff(start, 'months', true) / 3);
		const lastShownCycle = Number(
			localStorage.getItem(`${tenantId}_whitelist_last_cycle_shown`) ?? -1
		);
		return cyclesPassed > lastShownCycle;
	};

	const handleConfirmThreeMonthModal = () => {
		const start = moment(thresholdData?.first_campaign_created_at);
		const now = moment();
		const cyclesPassed = Math.floor(now.diff(start, 'months', true) / 3);
		localStorage.setItem(
			`${tenantId}_whitelist_last_cycle_shown`,
			String(cyclesPassed)
		);
		window.open(DEFAULT_WHITELIST_URL, '_blank', 'noopener,noreferrer');
		setOpenThreeMonthWhitelistingModal(false);
	};

	const dropdownIcon = (
		<div className="cs-dropdownWrapper">
			<Icon type="campaignSetupDropdown" />
		</div>
	);

	useEffect(() => {
		if (!!filterResults) {
			setLanguages([
				{ value: '', label: t('phishingManager.campaignSetup.step1.allLanguages')},
				...filterResults?.languages?.map(
					(el: { id: number; name: string }) => {
						return { value: el.name, label: el.name };
					}
				)
			]);
			setDifficulties([
				{ value: '', label: t('phishingManager.campaignSetup.step1.allDifficultes') },
				...(filterResults?.difficulties ?? []).map((el: string) => {
					const formatted =
						el.charAt(0).toUpperCase() + el.slice(1).toLowerCase();

					return {
						value: formatted,
						label: formatted
					};
				})
			]);
		}
	}, [filterResults, t]);

	useEffect(() => {
		if (!thresholdData || hasShownWhitelisting) return;
		const { first_campaign_created_at } = thresholdData;
		setOpenFirstWhitelistingModal(false);
		setOpenThreeMonthWhitelistingModal(false);
		if (!first_campaign_created_at) {
			setOpenFirstWhitelistingModal(true);
			setHasShownWhitelisting(true);
			return;
		}
		if (
			thresholdData?.first_campaign_created_at &&
			shouldShowThreeMonthModal(thresholdData.first_campaign_created_at)
		) {
			setOpenThreeMonthWhitelistingModal(true);
		}
	}, [thresholdData, hasShownWhitelisting]);

	useEffect(() => {
		setTemplates([]);
		setStep1State((prev: any) => ({ ...prev, templatePage: 1 }));
	}, [debouncedSearch, category, difficulty, language, sortOrder]);

	useEffect(() => {
		fetchCampaignsTemplates();
	}, [debouncedSearch, category, difficulty, language, templatePage, sortOrder]);

	useEffect(() => {
		return () => {
			debouncedSetSearch.cancel();
		};
	}, [debouncedSetSearch]);

	const sortOptions = useMemo(
		() => [
			{ label: t('phishingTemplateManager.publishedFilterToolbar.sortAZ', 'Sort: A-Z'), value: 'asc' },
			{ label: t('phishingTemplateManager.publishedFilterToolbar.sortZA', 'Sort: Z-A'), value: 'desc' }
		],
		[t]
	);

	const renderViewModeButtons = (extraClass = '') => (
		<div className={`pm-view-mode-container ${extraClass}`}>
			<button
				type="button"
				onClick={() => setViewMode('grid')}
				className={`pm-view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
				title={t('phishingTemplateManager.publishedFilterToolbar.gridView', 'Grid View')}
			>
				<span className={`pm-view-icon-inner ${viewMode === 'grid' ? 'active-icon' : 'inactive-icon'}`}>
					<Icon type="grid" />
				</span>
			</button>

			<button
				type="button"
				onClick={() => setViewMode('list')}
				className={`pm-view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
				title={t('phishingTemplateManager.publishedFilterToolbar.listView', 'List View')}
			>
				<span className={`pm-view-icon-inner ${viewMode === 'list' ? 'active-icon' : 'inactive-icon'}`}>
					<Icon type="list" />
				</span>
			</button>
		</div>
	);

	return (
		<Spin spinning={loading}>
			<div className="campaign-template-list step-card pm-step1-card-wrapper">
				<div className="card-header pm-step1-header">
					<div className="card-title">
						{t('phishingManager.campaignSetup.step1.title')}
					</div>
					<div className="card-subtitle">
						{t('phishingManager.campaignSetup.step1.subtitle')}
					</div>
				</div>

				{category && (
					<div className="pm-breadcrumb-container">
						<span onClick={onClose} className="pm-breadcrumb-link">
							{t('phishingTemplateManager.allTemplates', 'All templates')}
						</span>

						<div className="pm-breadcrumb-arrow">
							<svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M4.5 9L7.5 6L4.5 3" stroke="#001738" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</div>

						<span className="pm-breadcrumb-current">
							{category}
						</span>
					</div>
				)}

				<div className="pm-responsive-toolbar">
					<div className="pm-toolbar-left">
						<div className="pm-list-icon-badge">
							<span className="pm-list-icon-inner">
								<Icon type="list" />
							</span>
						</div>
						<span className="pm-toolbar-title-text">
							{category || t('phishingManager.campaignSetup.step1.allTemplates', 'All Templates')} ({totalTemplates})
						</span>
					</div>

					<div className="pm-toolbar-right">
						<div className="pm-search-row-mobile">
							<div className="pm-responsive-search pm-search-input-wrapper">
								<Input
									placeholder={t('phishingManager.campaignSetup.step1.search')}
									value={search}
									allowClear={false}
									bordered={false}
									onChange={(e) => {
										const value = e.target.value;
										setStep1State((prev: any) => ({
											...prev,
											search: value
										}));
										const searchValue = value.trimEnd();
										if (!searchValue.length || searchValue.length > 2) debouncedSetSearch(value);
									}}
								/>
								<span className="pm-search-icon">
									<Icon type="search" />
								</span>
							</div>
							{renderViewModeButtons('pm-responsive-view-mode')}
						</div>

						<div className="pm-dropdowns-row-mobile d-flex align-center gap-12">
							<Select
								className="custom-filter-select pm-responsive-select-lang"
								dropdownClassName="campaign-setup-filter-option-select"
								suffixIcon={dropdownIcon}
								value={language}
								onChange={(value) =>
									setStep1State((prev: any) => ({
										...prev,
										language: value
									}))
								}
								options={languages}
								getPopupContainer={(trigger) => trigger.parentElement || document.body}
							/>
							<Select
								className="custom-filter-select pm-responsive-select-diff"
								dropdownClassName="campaign-setup-filter-option-select"
								suffixIcon={dropdownIcon}
								value={difficulty}
								onChange={(value) =>
									setStep1State((prev: any) => ({
										...prev,
										difficulty: value
									}))
								}
								options={difficulties}
								getPopupContainer={(trigger) => trigger.parentElement || document.body}
							/>
							<Select
								className="custom-filter-select pm-responsive-select-sort"
								dropdownClassName="campaign-setup-filter-option-select"
								suffixIcon={<Icon type="arrowDownArrowUp" />}
								value={sortOrder}
								onChange={(value) => setSortOrder(value as 'asc' | 'desc')}
								options={sortOptions}
								getPopupContainer={(trigger) => trigger.parentElement || document.body}
							/>
						</div>

						{renderViewModeButtons('pm-desktop-view-mode')}
					</div>
				</div>

				<div className="template-list-section w-full">
					{templates.length === 0 && !loading ? (
						<div className="cs-empty-state">
							{t('phishingManager.campaignSetup.step1.emptyState')}
						</div>
					) : isMobile ? (
						<InfinityScrollWrapper
							dataLength={templates.length}
							hasMore={templates.length < totalTemplates}
							onLoadMore={() => {
								setStep1State((prev: any) => ({
									...prev,
									templatePage: prev.templatePage + 1
								}));
							}}
							height={600}
							scrollableId="template-scroll-container"
							loaderText={t('phishingManager.campaignSetup.step1.loadingMore')}
							endMessageText={t('phishingManager.campaignSetup.step1.allLoaded')}
						>
							<Cards
								templates={templates}
								selectedTemplate={selectedTemplate}
								onSelect={onSelect}
								viewMode={viewMode}
								isMobile={isMobile}
							/>
						</InfinityScrollWrapper>
					) : (
						<Cards
							templates={templates}
							selectedTemplate={selectedTemplate}
							onSelect={onSelect}
							viewMode={viewMode}
							isMobile={isMobile}
						/>
					)}
					{totalTemplates > pageSize && !isMobile && (
						<PaginationElement
							total={totalTemplates}
							pageSize={pageSize}
							current={templatePage}
							onChange={(page) =>
								setStep1State((prev: any) => ({
									...prev,
									templatePage: page
								}))
							}
						/>
					)}
				</div>

				<FirstWhitelistingModal
					open={openFirstWhitelistingModal}
					onClose={handleCloseFirstModal}
					onConfirm={handleConfirmFirstModal}
				/>
				<ThreeMonthWhitelistingModal
					open={openThreeMonthWhitelistingModal}
					onClose={handleCloseThreeMonthModal}
					onConfirm={handleConfirmThreeMonthModal}
				/>
			</div>
		</Spin>
	);
}

const Cards = ({
	templates,
	onSelect,
	selectedTemplate,
	viewMode,
	isMobile
}: {
	templates: Template[];
	onSelect: (value: string | number | null) => void;
	selectedTemplate: string | number | null;
	viewMode: 'grid' | 'list';
	isMobile: boolean;
}) => {
	const { t } = useTranslation();

	if (viewMode === 'list') {
		return (
			<div className={`pm-list-container-grid ${isMobile ? 'mobile' : ''}`}>
				{templates.map((tItem) => {
					const isSelected = selectedTemplate === tItem.value;
					const categoryText = joinItems(tItem.categories);

					return (
						<div key={tItem.value} className={`pm-list-item-card ${isMobile ? 'mobile' : ''}`}>
							<div className="pm-list-item-img-box">
								<img
									src={tItem.img || './placeholderImage.png'}
									alt={tItem.label}
								/>
							</div>

							<div className="pm-list-item-content">
								<div className="pm-list-item-title-row">
									<div className="pm-list-item-title" title={tItem.label}>
										{tItem.label}
									</div>
								</div>
								<div className="pm-list-item-subtext">
									{categoryText}
								</div>
							</div>

							<div className="pm-list-item-action">
								<Button
									shape="round"
									type="primary"
									onClick={(e) => {
										e.stopPropagation();
										isSelected ? onSelect(null) : onSelect(tItem.value);
									}}
									>
									{isSelected 
										? t('phishingManager.campaignSetup.step1.templateCard.selected', 'Selected') 
										: t('select', 'Select')
									}
								</Button>
							</div>
						</div>
					);
				})}
			</div>
		);
	}

	return (
		<div className="cs-template-list-section">
			{templates.map((tItem) => (
				<TemplateCard
					key={tItem.value}
					template={tItem}
					selected={selectedTemplate === tItem.value}
					onSelect={onSelect}
				/>
			))}
		</div>
	);
};
